import * as React from 'react'

import { ResourceFieldContext } from '~/client/src/desktop/components/ResourceFieldConfigurator/ResourceFieldContext'
import { Button } from '~/client/src/shared/components/Button/Button'
import { ListSelector } from '~/client/src/shared/components/ListSelector'
import { IResourceProperty } from '~/client/src/shared/components/ResourceCardList/types'
import { fromCompoundIds } from '~/client/src/shared/components/ResourcePicker/filterCompoundIds'
import {
  ILocalDefaults,
  IParsedDefaults,
} from '~/client/src/shared/components/ResourcePicker/parseDefaultOptions'
import ResourcePicker from '~/client/src/shared/components/ResourcePicker/ResourcePicker'
import { useResourcePickerContext } from '~/client/src/shared/components/ResourcePicker/ResourcePickerContext'
import {
  ResourcePickerCard,
  useResourceIcon,
} from '~/client/src/shared/components/ResourcePicker/useResourceRenderItem'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import { IResourceRecord } from '~/client/src/shared/resources/definitions/types'

const NO_DEFAULTS: IParsedDefaults = {}

interface IProps {
  resourceTypeId: string
  resourceLabel: string
  records: IResourceRecord[]
  selectedIds: string[]
  cardConfiguration: {
    subtitleProperty: string | null
    displayProperties: string[]
  }
  properties: IResourceProperty[]
  propertiesReady: boolean
  loading: boolean
  getDisplayValue: (propertyName: string, value: unknown) => string
  recordPredicate?: (record: IResourceRecord) => boolean
  onApply: (ids: string[]) => void
  onClose: () => void
  /**
   * Rendered in its own bordered row above the buttons, like the "more resources" link -
   * for saying what Save will do.
   */
  footerNote?: React.ReactNode
  /** Save may commit an empty selection. */
  allowEmptySelection?: boolean
}

const RestrictionFooter: React.FC<{
  onApply: (ids: string[]) => void
  onClose: () => void
  allowEmptySelection?: boolean
}> = ({ onApply, onClose, allowEmptySelection }) => {
  const { selectedIds, hasSelection } = useResourcePickerContext()

  const handleApply = React.useCallback(() => {
    onApply(Array.from(selectedIds))
  }, [selectedIds, onApply])

  return (
    <div className="tw:flex tw:justify-end tw:items-center tw:gap-3 tw:p-2.5 tw:border-0 tw:border-t tw:border-solid tw:border-input-soft">
      <Button variant="outline" onClick={onClose}>
        {Localization.translator.cancel}
      </Button>
      <Button
        variant="default"
        onClick={handleApply}
        disabled={!hasSelection && !allowEmptySelection}
      >
        {Localization.translator.save}
      </Button>
    </div>
  )
}

const ResourceSelectionContent: React.FC<IProps> = ({
  resourceTypeId,
  resourceLabel,
  records,
  selectedIds,
  cardConfiguration,
  properties,
  propertiesReady,
  loading,
  getDisplayValue,
  recordPredicate,
  onApply,
  onClose,
  footerNote,
  allowEmptySelection,
}) => {
  const icon = useResourceIcon(resourceTypeId)
  const fieldCtx = React.useContext(ResourceFieldContext)

  const defaults = React.useMemo<IParsedDefaults>(() => {
    if (!fieldCtx) return NO_DEFAULTS
    return {
      sortKey: fieldCtx.sortKey,
      sortOrder: fieldCtx.sortOrder,
      // Unset previews what the form opens with: the resource's picker default.
      groupByKey: fieldCtx.groupByKey ?? undefined,
      filterSelection: fieldCtx.filterSelection.length
        ? fromCompoundIds(fieldCtx.filterSelection)
        : undefined,
    }
  }, [
    fieldCtx?.sortKey,
    fieldCtx?.sortOrder,
    fieldCtx?.groupByKey,
    fieldCtx?.filterSelection,
  ])

  const setAllDefaults = fieldCtx?.setAllDefaults
  const handleDefaultsChange = React.useCallback(
    (snapshot: ILocalDefaults) => {
      setAllDefaults?.(
        snapshot.sortKey,
        snapshot.sortOrder,
        snapshot.groupByKey === 'none' ? null : snapshot.groupByKey,
        snapshot.filterSelection,
      )
    },
    [setAllDefaults],
  )

  return (
    <div
      data-resource-picker-root
      className="tw:relative tw:w-[400px] tw:h-[min(80vh,var(--available-height,80vh))] tw:flex tw:flex-col"
    >
      <ListSelector.Title
        title={`${Localization.translator.select} ${resourceLabel}`}
        onClose={onClose}
      />
      <ResourcePicker
        records={records}
        resourceTypeId={resourceTypeId}
        mode="multi"
        selectedIds={selectedIds}
        onSelect={onApply}
        properties={properties}
        propertiesReady={propertiesReady}
        loading={loading}
        defaults={defaults}
        recordPredicate={recordPredicate}
        onDefaultsChange={fieldCtx ? handleDefaultsChange : undefined}
      >
        <ResourcePicker.Toolbar resetToEmpty={!!fieldCtx} />
        <ResourcePicker.List
          portalSelector="[data-resource-picker-root]"
          renderItem={record => (
            <ResourcePickerCard
              record={record}
              resourceTypeId={resourceTypeId}
              icon={icon}
              cardConfiguration={cardConfiguration}
              properties={properties}
              getDisplayValue={getDisplayValue}
            />
          )}
          countLabel={resourceLabel}
        />
        <ResourcePicker.EmptyState />
        {!!footerNote && (
          <div className="tw:shrink-0 tw:px-3 tw:py-2 tw:border-0 tw:border-t tw:border-solid tw:border-input-soft">
            <p className="tw:m-0 tw:text-body-sm tw:text-muted-foreground">
              {footerNote}
            </p>
          </div>
        )}
        <RestrictionFooter
          onApply={onApply}
          onClose={onClose}
          allowEmptySelection={allowEmptySelection}
        />
      </ResourcePicker>
    </div>
  )
}

ResourceSelectionContent.displayName = 'ResourceSelectionContent'

export default ResourceSelectionContent
